import * as React from 'react';
import { Image } from '@zoopla/image';
import { SavedLarge, SaveLarge } from '@zoopla-eng/zds/icons';
import { ListingImage } from '@zoopla/listings/image';
import { ListingFeatureList } from '@zoopla/listings/features';

import {
    ListingCardPortraitMiniIconType,
    ListingCardPortraitMiniProps
} from './ListingCardPortraitMini.types';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { IconButton } from '@zoopla-eng/zds/button';
import {
    agencyLogoImageStyle,
    agencyLogoStyle,
    badgeWrapperStyle,
    cardDetailsStyle,
    cardHeaderStyle,
    cardLinkStyle,
    featuresContainerStyle,
    iconContainerStyle,
    imageContainerStyle,
    priceStyle,
    priceTitleTextStyle,
    styledCardStyle,
    styledTitleStyle
} from './ListingCardPortraitMini.styles.css';

const defaultHandler = () => null;

const ListingCardPortraitMini: React.FC<
    React.PropsWithChildren<ListingCardPortraitMiniProps>
> = ({
    link,
    address,
    features,
    image,
    branch,
    price,
    priceTitle,
    title,
    badge,
    showIconType = ListingCardPortraitMiniIconType.BranchIcon,
    isListingSaved = false,
    onImageClick = defaultHandler,
    onCardClick = defaultHandler,
    onFeaturesClick = defaultHandler,
    onTitleClick = defaultHandler,
    onPriceClick = defaultHandler,
    onSaveClick = defaultHandler,
    onAgentClick = defaultHandler,
    onAddressClick = defaultHandler,
    onLinkClick = defaultHandler
}) => {
    const buildCardIcon = () => {
        if (showIconType === ListingCardPortraitMiniIconType.BranchIcon) {
            if (branch?.logoUrl) {
                return (
                    <div className={agencyLogoStyle}>
                        <div className={agencyLogoImageStyle}>
                            <Image
                                onClick={onAgentClick}
                                data-testid={'agent-img'}
                                src={branch?.logoUrl}
                                alt={`Marketed by ${branch?.name}`}
                            />
                        </div>
                    </div>
                );
            }
        } else if (
            showIconType === ListingCardPortraitMiniIconType.FavouriteIcon
        ) {
            const Icon = isListingSaved ? SavedLarge : SaveLarge;
            return (
                <div className={iconContainerStyle}>
                    <IconButton
                        onClick={onSaveClick}
                        data-testid="favourite"
                        category="tertiary"
                        label="Menu"
                    >
                        {Icon}
                    </IconButton>
                </div>
            );
        }
        return null;
    };

    const cardIcon = buildCardIcon();

    return (
        <div
            className={styledCardStyle}
            data-testid={'listing-card-portrait-mini'}
            onClick={onCardClick}
        >
            <a
                className={cardLinkStyle}
                href={link}
                onClick={onLinkClick}
                data-testid="link"
                target="_blank"
                rel="noreferrer"
            >
                <div className={imageContainerStyle} onClick={onImageClick}>
                    <ListingImage image={image} />
                    {badge && <div className={badgeWrapperStyle}>{badge}</div>}
                </div>
                <div className={cardDetailsStyle}>
                    <div
                        className={cardHeaderStyle}
                        data-testid="price-title"
                        onClick={onPriceClick}
                    >
                        <div>
                            {priceTitle && (
                                <p
                                    className={`${fontGroups.ladle} ${priceTitleTextStyle}`}
                                >
                                    {priceTitle}
                                </p>
                            )}
                            <p className={`${fontGroups.mug} ${priceStyle}`}>
                                {price}
                            </p>
                        </div>
                    </div>
                    {cardIcon}
                    <div
                        className={featuresContainerStyle}
                        data-testid={'features'}
                        onClick={onFeaturesClick}
                    >
                        <ListingFeatureList features={features} noSpacing />
                    </div>

                    {title && (
                        <div className={styledTitleStyle}>
                            <h3
                                className={fontGroups.ladle}
                                data-testid="title"
                                onClick={onTitleClick}
                            >
                                {title}
                            </h3>
                        </div>
                    )}
                    {address && (
                        <p
                            className={fontGroups.knife}
                            data-testid={'address'}
                            onClick={onAddressClick}
                        >
                            {address}
                        </p>
                    )}
                </div>
            </a>
        </div>
    );
};

export default ListingCardPortraitMini;
